/**
 * Call waiting (web): a second call rings while this person is in a call.
 *
 * One banner at the top of the call (web-vrs.html frame 2, approved
 * 2026-10-29): the caller's name, "Video call · 18 s", then Hold and answer
 * (primary) · Decline · VideoMail · End and answer. Robert, 2026-10-12: "add
 * hold screen to web portal". The new call opens with a full page load; the
 * held call is kept in storage (vrs_held_call, call-management/middleware.ts),
 * so the next page shows it in the call's bar (HeldCallBar.web) and on web
 * Home (client-profile.js) with Resume. No Hold where the server says so
 * (holdAllowed: false; a call with an interpreter, for the person billed).
 * VideoMail only where the server can give the caller the VideoMail offer
 * (ringPresentation.ts canSendRingToVideoMail). Vibrates where it can.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { makeStyles } from 'tss-react/mui';
import { isFeatureEnabled } from '../../base/whitelabel/functions';
import { queueService } from '../../interpreter-queue/InterpreterQueueService';
import { callWaitingRespond, callWaitingDismiss } from '../actions';
import { startCallAttention } from '../deafAccessibleAlert';
import { RING_WINDOW_MS, canSendRingToVideoMail, callerInitials, ringEndsAt, ringSecondsLeft } from './ringPresentation';
/** How long a response may go unconfirmed before the card offers a retry. */
const RESPONSE_TIMEOUT_MS = 10000;
/** How long "Answered on another device." stays before the card goes. */
const ENDED_NOTICE_MS = 2000;
/** The server's one-winner refusals (docs/single-winner-answers.md). */
const ANSWER_LOST = ['ANSWERED_ELSEWHERE', 'ALREADY_TAKEN'];
const useStyles = makeStyles()(theme => {
    return {
        banner: {
            background: theme.palette.ui02,
            border: `1px solid ${theme.palette.ui03}`,
            borderRadius: 18,
            boxShadow: '0 12px 32px rgba(0, 0, 0, 0.4)',
            boxSizing: 'border-box',
            color: theme.palette.text01,
            insetInline: 12,
            margin: '0 auto',
            maxWidth: 560,
            padding: 12,
            position: 'absolute',
            top: 12,
            zIndex: 10000
        },
        caller: {
            alignItems: 'center',
            display: 'flex',
            gap: 10
        },
        mark: {
            alignItems: 'center',
            background: theme.palette.ui03,
            borderRadius: '50%',
            display: 'flex',
            flex: 'none',
            fontSize: 15,
            fontWeight: 600,
            height: 44,
            justifyContent: 'center',
            overflow: 'hidden',
            width: 44,
            '& img': {
                height: '100%',
                objectFit: 'cover',
                width: '100%'
            }
        },
        who: {
            flex: 1,
            minWidth: 0
        },
        name: {
            fontSize: 16,
            fontWeight: 600,
            margin: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
        },
        detail: {
            color: theme.palette.text02,
            fontSize: 13,
            fontVariantNumeric: 'tabular-nums',
            margin: 0
        },
        status: {
            color: theme.palette.text02,
            fontSize: 13,
            margin: '6px 0 0',
            '&:empty': {
                display: 'none'
            }
        },
        actions: {
            display: 'grid',
            gap: 6,
            gridTemplateColumns: 'repeat(auto-fit, minmax(104px, 1fr))',
            marginTop: 10
        },
        button: {
            background: 'transparent',
            border: `1px solid ${theme.palette.ui04}`,
            borderRadius: 12,
            color: theme.palette.text01,
            cursor: 'pointer',
            fontSize: 14,
            fontWeight: 600,
            minHeight: 44,
            padding: '0 8px',
            '&:disabled': {
                cursor: 'default',
                opacity: 0.5
            },
            '&:focus-visible': {
                outline: `2px solid ${theme.palette.focus01}`,
                outlineOffset: 2
            }
        },
        primary: {
            background: theme.palette.action01,
            borderColor: theme.palette.action01
        }
    };
});
const CallWaitingOverlay = () => {
    const { classes, cx } = useStyles();
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const incomingCall = useSelector((state) => state['features/call-management']?.incomingCall);
    const [pending, setPending] = useState('');
    const [error, setError] = useState('');
    const [ended, setEnded] = useState(false);
    const [now, setNow] = useState(() => Date.now());
    const [endsAt, setEndsAt] = useState(0);
    const pendingRef = useRef('');
    const responseTimer = useRef(undefined);
    const settle = () => {
        pendingRef.current = '';
        setPending('');
        if (responseTimer.current) {
            clearTimeout(responseTimer.current);
            responseTimer.current = undefined;
        }
    };
    // Deaf-accessible alert: vibrate where supported (mobile), and flash the
    // tab title on desktop where vibration is a no-op. The title flash clears
    // when the waiting call is answered, rejected, dismissed, or the caller
    // gives up — this card must never outlive the ring it announces.
    useEffect(() => {
        settle();
        setError('');
        setEnded(false);
        if (!incomingCall) {
            return undefined;
        }
        if (typeof navigator.vibrate === 'function') {
            navigator.vibrate([200, 100, 200, 100, 200]);
        }
        // "Video call · 18 s": the seconds left on this ring.
        const receivedAt = Date.now();
        setNow(receivedAt);
        setEndsAt(ringEndsAt(incomingCall, receivedAt));
        const clock = setInterval(() => setNow(Date.now()), 1000);
        const stop = startCallAttention({
            callerName: incomingCall.callerName || t('vrs.ring.unknownCaller'),
            body: 'Call waiting on your current call.'
        });
        let endedTimer;
        const matches = (data) => Boolean(data) && typeof data === 'object'
            && data.callId === incomingCall.callId;
        const gone = (data) => {
            if (matches(data))
                dispatch(callWaitingDismiss());
        };
        const responded = (data) => {
            if (matches(data)) {
                settle();
                dispatch(callWaitingDismiss());
            }
        };
        // One answer wins a ring (docs/single-winner-answers.md): another
        // device of this person answered first. Say so, then the card goes.
        const lost = () => {
            if (endedTimer)
                return;
            settle();
            stop();
            setError('');
            setEnded(true);
            endedTimer = setTimeout(() => dispatch(callWaitingDismiss()), ENDED_NOTICE_MS);
        };
        const refused = (data) => {
            const callId = data.callId;
            if (data.retrying || (typeof callId === 'string' && callId !== incomingCall.callId))
                return;
            if (data.code && ANSWER_LOST.includes(data.code) && (pendingRef.current || callId)) {
                lost();
                return;
            }
            if (!pendingRef.current)
                return;
            settle();
            setError(data.message || t('vrs.ring.notConfirmed'));
        };
        // The device that won is never told the ring ended, so an answer
        // still on its way here lost the race.
        const ringEnded = (data) => {
            if (data.callId !== incomingCall.callId)
                return;
            if (data.reason === 'answered_elsewhere') {
                lost();
                return;
            }
            if (!pendingRef.current || (data.reason !== 'timeout' && data.reason !== 'declined_elsewhere')) {
                dispatch(callWaitingDismiss());
            }
        };
        queueService.on('p2pCallCancelled', gone);
        queueService.on('p2pCallEnded', gone);
        queueService.on('callWaitingResponded', responded);
        queueService.on('error', refused);
        queueService.on('ringEnded', ringEnded);
        const expiry = setTimeout(() => {
            if (!pendingRef.current)
                dispatch(callWaitingDismiss());
        }, RING_WINDOW_MS);
        return () => {
            stop();
            clearInterval(clock);
            clearTimeout(expiry);
            clearTimeout(endedTimer);
            settle();
            queueService.off('p2pCallCancelled', gone);
            queueService.off('p2pCallEnded', gone);
            queueService.off('callWaitingResponded', responded);
            queueService.off('error', refused);
            queueService.off('ringEnded', ringEnded);
        };
    }, [incomingCall?.callId, dispatch]);
    if (!incomingCall) {
        return null;
    }
    const respond = (action) => {
        if (pendingRef.current || ended)
            return;
        setError('');
        pendingRef.current = action;
        setPending(action);
        try {
            dispatch(callWaitingRespond(incomingCall.callId, incomingCall.currentCallId, action));
        }
        catch {
            settle();
            setError(t('vrs.ring.notSent'));
            return;
        }
        // Reject is settled by the reducer (it clears the card); the accepts
        // wait for call_waiting_responded so a dropped answer is visible.
        responseTimer.current = setTimeout(() => {
            if (pendingRef.current !== action)
                return;
            settle();
            setError(t('vrs.ring.notConfirmed'));
        }, RESPONSE_TIMEOUT_MS);
    };
    const handleAccept = () => respond('accept');
    const handleHold = () => respond('hold_and_accept');
    const handleReject = () => respond('reject');
    // The portal's "Send to VideoMail": a decline that gives the caller the
    // VideoMail offer at once (ws/p2p-handlers.js handleP2PDecline).
    const handleVideoMail = () => {
        if (pendingRef.current || ended)
            return;
        try {
            queueService.declineP2PCall({
                callId: incomingCall.callId,
                callerId: incomingCall.callerId,
                sendToVoicemail: true
            });
        }
        catch {
            setError(t('vrs.ring.notSent'));
            return;
        }
        dispatch(callWaitingDismiss());
    };
    const busy = Boolean(pending) || ended;
    const callerName = incomingCall.callerName || t('vrs.ring.unknownCaller');
    const videoMail = canSendRingToVideoMail(incomingCall, isFeatureEnabled('voicemail'));
    const kind = incomingCall.groupCall ? t('vrs.ring.groupCall') : t('vrs.ring.videoCall');
    const seconds = endsAt ? ringSecondsLeft(endsAt, now) : RING_WINDOW_MS / 1000;
    const joining = t('vrs.ring.joining');
    return (React.createElement("div", { "aria-label": t('vrs.ring.calling', { name: callerName }), className: cx('call-waiting-banner', classes.banner), role: 'alertdialog' },
        React.createElement("div", { className: classes.caller },
            React.createElement("div", { "aria-hidden": 'true', className: classes.mark }, incomingCall.callerPhotoUrl
                ? React.createElement("img", { alt: '', decoding: 'async', src: incomingCall.callerPhotoUrl })
                : callerInitials(callerName)),
            React.createElement("div", { className: classes.who },
                React.createElement("p", { className: cx('call-waiting-caller', classes.name) }, callerName),
                React.createElement("p", { className: classes.detail }, `${kind} · ${t('vrs.ring.secondsLeft', { seconds })}`))),
        React.createElement("p", { "aria-live": 'polite', className: 'call-waiting-status' }, ended ? t('vrs.ring.answeredElsewhere') : error || (pending ? joining : '')),
        React.createElement("div", { className: classes.actions },
            incomingCall.holdAllowed === false ? null : (React.createElement("button", { "aria-busy": pending === 'hold_and_accept', className: cx('call-waiting-btn hold', classes.button, classes.primary), disabled: busy, onClick: handleHold, type: 'button' }, pending === 'hold_and_accept' ? joining : t('vrs.ring.holdAndAnswer'))),
            React.createElement("button", { className: cx('call-waiting-btn reject', classes.button), disabled: busy, onClick: handleReject, type: 'button' }, t('vrs.ring.decline')),
            videoMail && (React.createElement("button", { className: cx('call-waiting-btn videomail', classes.button), disabled: busy, onClick: handleVideoMail, type: 'button' }, t('vrs.ring.videoMail'))),
            React.createElement("button", { "aria-busy": pending === 'accept', className: cx('call-waiting-btn accept', classes.button, incomingCall.holdAllowed === false && classes.primary), disabled: busy, onClick: handleAccept, type: 'button' }, pending === 'accept' ? joining : t('vrs.ring.endAndAnswer')))));
};
export default CallWaitingOverlay;
